import type {
    ReactiveDropdownCatalogPhase,
    ReactiveFormSupportDataSource,
} from '@fitogest/reactive-form-builder/interfaces';
import type { TareaFitoModel } from 'src/app/models/tareas/tarea-fito.model';
import type { FitosanitariosEnvironmentDefinition } from '../../environment-variables';

export const FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS = {
    clientes: 'clientes',
    tratamientos: 'tratamientos',
    justificaciones: 'justificaciones',
    maquinarias: 'maquinarias',
    trabajadores: 'trabajadores',
    usuarios: 'usuarios',
    productos: 'productos',
} as const;

export type FitosanitariosFormSupportDataKey =
    typeof FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS[keyof typeof FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS];

export interface FitosanitariosFormSupportDataSourceConfig {
    readonly formType: number;
    readonly env: FitosanitariosEnvironmentDefinition;
}

const CREATION_FORM_TYPE = 0;

/** Shared load-plan rules for Fitosanitarios support data. */
export class FitosanitariosFormLoadPlan {
    public static createSupportDataSources(
        config: FitosanitariosFormSupportDataSourceConfig,
    ): readonly ReactiveFormSupportDataSource<TareaFitoModel>[] {
        const primaryPhase = this.resolvePrimarySupportDataPhase(config.formType);
        const sources: ReactiveFormSupportDataSource<TareaFitoModel>[] = [
            this.createSupportDataSource(FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.clientes, 'deferred'),
            this.createSupportDataSource(FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.tratamientos, 'lazy'),
            this.createSupportDataSource(FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.justificaciones, 'deferred'),
            this.createSupportDataSource(FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.maquinarias, primaryPhase),
            this.createSupportDataSource(FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.trabajadores, primaryPhase),
        ];

        if (config.env.showAssignUserTasksFitosanitario || config.env.showAssignUserInAllTasks) {
            sources.push(this.createSupportDataSource(FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.usuarios, primaryPhase));
        }

        if (config.env.appName === 'innovia') {
            sources.push(this.createSupportDataSource(
                FITOSANITARIOS_FORM_SUPPORT_DATA_KEYS.productos,
                config.formType === CREATION_FORM_TYPE ? 'lazy' : 'critical',
            ));
        }

        return sources;
    }

    private static resolvePrimarySupportDataPhase(formType: number): ReactiveDropdownCatalogPhase {
        return formType === CREATION_FORM_TYPE ? 'deferred' : 'critical';
    }

    private static createSupportDataSource(
        key: FitosanitariosFormSupportDataKey,
        phase: ReactiveDropdownCatalogPhase,
    ): ReactiveFormSupportDataSource<TareaFitoModel> {
        return {
            key,
            phase,
            load: () => undefined,
        };
    }
}
